/**
 * Mobile arc Part A (feel-it spike) — pure run-capture math.
 *
 * No React, no platform APIs: everything here is deterministic over
 * arrays of GeoPoints so it unit-tests without a device. The tracker
 * (tracker.ts) feeds points in; the live-run screen renders the
 * derived stats. Display units are miles / min-per-mile (US HS XC
 * convention, matching the rest of the app).
 */

export interface GeoPoint {
  lat: number;
  lng: number;
  /** Meters above sea level; null when the fix has no altitude. */
  alt: number | null;
  /** Epoch ms. */
  ts: number;
  /** Horizontal accuracy in meters (68% confidence radius). */
  accuracy: number;
}

export const METERS_PER_MILE = 1609.344;
const EARTH_RADIUS_M = 6371008.8;

/** Fixes worse than this are noise, not movement (GPS warm-up, tunnels). */
export const MAX_ACCURACY_M = 35;

/** Ignore altitude wiggle below this when summing climb. First outdoor
 *  test fabricated 192 ft on flat ground at 3 m — GPS altitude jitter
 *  runs ±5 m+. Part B replaces this with real smoothing/barometer. */
const ELEVATION_NOISE_M = 6;

export function haversineMeters(a: GeoPoint, b: GeoPoint): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(s));
}

/** Keep only fixes precise enough to measure a run with. Accuracy and
 *  finiteness are different questions (B199 fix2) — a fix can carry a
 *  tight accuracy radius while still having a non-finite lat/lng (a real
 *  GPS/bridge edge case), and every consumer of usable points (distance,
 *  the summary route, the save payload) assumes finite coordinates. The
 *  route surface also drops a non-finite fix at ingestion
 *  (`onPoint`, routes/run-spike.tsx) so this is the belt, not the only
 *  layer. */
export function usablePoints(points: GeoPoint[]): GeoPoint[] {
  return points.filter(
    (p) => p.accuracy <= MAX_ACCURACY_M && Number.isFinite(p.lat) && Number.isFinite(p.lng),
  );
}

export function totalDistanceMeters(points: GeoPoint[]): number {
  const pts = usablePoints(points);
  let meters = 0;
  for (let i = 1; i < pts.length; i++) {
    meters += haversineMeters(pts[i - 1], pts[i]);
  }
  return meters;
}

/**
 * Climb only (descents don't subtract), with a noise floor so ±1–2 m
 * altitude jitter between fixes doesn't fabricate elevation.
 */
export function elevationGainMeters(points: GeoPoint[]): number {
  const pts = usablePoints(points).filter((p) => p.alt !== null);
  let gain = 0;
  let last: number | null = null;
  for (const p of pts) {
    const alt = p.alt as number;
    if (last !== null) {
      const diff = alt - last;
      if (diff >= ELEVATION_NOISE_M) {
        gain += diff;
        last = alt;
      } else if (diff <= -ELEVATION_NOISE_M) {
        last = alt;
      }
      // within the noise band: keep `last` anchored so slow real climbs
      // still accumulate once they clear the floor.
    } else {
      last = alt;
    }
  }
  return gain;
}

/**
 * Current pace in seconds per mile over the trailing window, or null
 * until there's enough movement to be meaningful (avoids the wild
 * paces a 3-meter first step implies).
 */
export function rollingPaceSecPerMile(
  points: GeoPoint[],
  windowMs = 45_000,
  // Below ~35 m per 45 s window (≈ slower than a 35 min/mi crawl) the
  // "movement" is stationary GPS wander, not locomotion — show no pace.
  // First outdoor test: standing wander read as a phantom 39:58/mi.
  minMeters = 35,
): number | null {
  const pts = usablePoints(points);
  if (pts.length < 2) return null;
  const end = pts[pts.length - 1];
  const windowPts = pts.filter((p) => end.ts - p.ts <= windowMs);
  if (windowPts.length < 2) return null;
  const meters = totalDistanceMeters(windowPts);
  const seconds = (windowPts[windowPts.length - 1].ts - windowPts[0].ts) / 1000;
  if (meters < minMeters || seconds <= 0) return null;
  return seconds / (meters / METERS_PER_MILE);
}

export function formatPace(secPerMile: number | null): string {
  if (secPerMile === null || !Number.isFinite(secPerMile) || secPerMile > 3600) {
    return '—';
  }
  const m = Math.floor(secPerMile / 60);
  const s = Math.round(secPerMile % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

export function formatMiles(meters: number): string {
  return (meters / METERS_PER_MILE).toFixed(2);
}

export function metersToFeet(meters: number): number {
  return meters * 3.28084;
}

/**
 * Part B save flow — the route we UPLOAD is a bounded, evenly-sampled
 * polyline (first + last kept). The client pre-caps payload size; the
 * server re-samples to its own runtime-tunable budget
 * (api/lib/planning/runCapture.js — same algorithm). The raw
 * second-by-second trace never leaves the phone (founder-ratified).
 *
 * RC-v2 (2026-07-06): each vertex is a rich 4-tuple [lng, lat, ele, tRel]
 * — ele = altitude in meters (null when a fix has none), tRel = seconds
 * since the first usable point. Backward-compatible with the legacy
 * [lng, lat] rows (the server validator + replay reader accept both); the
 * richer capture is what the altitude / pace-at-point features build on.
 */
export function routeForUpload(
  points: GeoPoint[],
  maxPoints = 2000,
): RouteCoord[] {
  const usable = usablePoints(points);
  const originTs = usable.length ? usable[0].ts : 0;
  const coords = usable.map((p): RouteCoord => [
    p.lng,
    p.lat,
    p.alt == null ? null : Math.round(p.alt * 10) / 10,
    Math.max(0, Math.round((p.ts - originTs) / 1000)),
  ]);
  if (coords.length <= maxPoints) return coords;
  if (maxPoints < 2) return coords.slice(0, 1);
  const out: RouteCoord[] = [];
  const step = (coords.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i++) {
    out.push(coords[Math.round(i * step)]);
  }
  return out;
}

/** A saved-route vertex as stored/served: legacy [lng, lat] (existing
 *  rows) or RC-v2 [lng, lat, ele, tRel] (ele meters/nullable, tRel secs).
 *  Consumers that only need position read [0]/[1] on either shape. */
export type RouteCoord = [number, number] | [number, number, number | null, number];

function lngLatMeters(a: RouteCoord, b: RouteCoord): number {
  return haversineMeters(
    { lat: a[1], lng: a[0], alt: null, ts: 0, accuracy: 0 },
    { lat: b[1], lng: b[0], alt: null, ts: 0, accuracy: 0 },
  );
}

/**
 * Detail/replay [9] — cumulative distance (meters) at each vertex of a
 * saved [lng, lat] polyline. cum[0] is 0; cum[last] is the route total.
 * Precomputed once per replay so per-frame scrubbing stays cheap.
 */
export function routeCumulativeMeters(coords: RouteCoord[]): number[] {
  const cum: number[] = [];
  let total = 0;
  for (let i = 0; i < coords.length; i++) {
    if (i > 0) total += lngLatMeters(coords[i - 1], coords[i]);
    cum.push(total);
  }
  return cum;
}

/**
 * The traveled prefix of a route at a distance fraction (0..1, clamped):
 * every vertex already passed plus one point linearly interpolated into
 * the bracketing segment. The last element is where the replay dot sits.
 * Degenerate routes (empty / single point / zero length) pin to the start.
 */
export function routeSliceAtFraction(
  coords: RouteCoord[],
  cum: number[],
  fraction: number,
): RouteCoord[] {
  if (coords.length === 0) return [];
  if (coords.length === 1) return [coords[0]];
  const total = cum[cum.length - 1];
  if (total <= 0) return coords.slice(0, 1);
  const f = Math.min(1, Math.max(0, fraction));
  const target = f * total;
  let i = 1;
  while (i < cum.length - 1 && cum[i] < target) i++;
  const segLen = cum[i] - cum[i - 1];
  const t = segLen > 0 ? Math.min(1, Math.max(0, (target - cum[i - 1]) / segLen)) : 1;
  const a = coords[i - 1];
  const b = coords[i];
  const end: RouteCoord = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  return [...coords.slice(0, i), end];
}
